/**
 * Le 409 `GED_FOLDER_HOLDS_PINNED_FILES`, lu par son CODE : un dossier qui porte une pièce ÉPINGLÉE.
 *
 * Le serveur (`_deletion_guards_cascade_epingles`) refuse la suppression d'un dossier dont la
 * cascade mettrait à la corbeille un fichier qu'un objet métier utilise : la pièce d'une facture
 * d'intervenant, la preuve d'un point de checklist, une pièce de checklist d'appel d'offres, une
 * pièce de la pochette, l'attestation d'une référence de marché. Les cinq portes qui suppriment
 * un dossier le lèvent : le coffre, et l'onglet Documents des fiches client, intervenant,
 * non-conformité et matériel.
 *
 * Le serveur envoie le nombre de fichiers (`files_count`) et le nom du premier (`file_name`) :
 * la phrase les cite, pour que l'utilisateur sache quel document sortir du dossier. Sans nom,
 * `null` : l'appelant retombe sur le message du serveur, qui reste juste.
 *
 * ⚠️ La clé est écrite EN ENTIER, DANS l'appel à `t` : une clé construite échapperait à tout
 * extracteur, et une clé absente des deux catalogues laisse la parité verte.
 */

import type { TFunction } from 'i18next'

/** Le code du refus : `_deletion_guards_cascade_epingles.GED_FOLDER_HOLDS_PINNED_FILES`. */
export const GED_FOLDER_HOLDS_PINNED_FILES = 'GED_FOLDER_HOLDS_PINNED_FILES'

/** Les phrases françaises du serveur, mot pour mot : le repli quand i18next n'est pas prêt. */
const UN_FR =
    "Ce dossier ne peut pas être supprimé : il contient « {{name}} », un document qui sert à un autre élément (par exemple une facture d'intervenant, une checklist ou la pochette de pièces permanentes). Sortez-le du dossier, ou détachez-le de cet élément, puis supprimez le dossier."
const PLUSIEURS_FR =
    "Ce dossier ne peut pas être supprimé : il contient {{count}} documents qui servent à d'autres éléments (par exemple une facture d'intervenant, une checklist ou la pochette de pièces permanentes), à commencer par « {{name}} ». Sortez-les du dossier, ou détachez-les de ces éléments, puis supprimez le dossier."

/** La phrase du refus d'un dossier qui porte une pièce épinglée, ou `null` si ce n'en est pas un. */
export function phraseDUnDossierQuiPorteUnePieceEpinglee(t: TFunction, erreur: unknown): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    const { code, details } = erreur as {
        code?: unknown
        details?: { files_count?: unknown; file_name?: unknown }
    }
    if (code !== GED_FOLDER_HOLDS_PINNED_FILES) return null
    const name = details?.file_name
    if (typeof name !== 'string' || !name.trim()) return null
    const nombre = details?.files_count
    const count = typeof nombre === 'number' && nombre > 0 ? nombre : 1
    const phrase: unknown = t('ged.refus.folder_holds_pinned_files', {
        defaultValue: count === 1 ? UN_FR : PLUSIEURS_FR,
        count,
        name,
    })
    return typeof phrase === 'string' && phrase.trim() ? phrase : null
}
